import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Container,
  Box,
  Typography,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Tooltip,
  Stack,
  FormGroup,
  Checkbox,
  IconButton,
  Menu,
  ListItemIcon,
  Badge,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { 
  AddCircleOutline, 
  DateRange, 
  EventAvailable, 
  Timeline, 
  AttachMoney, 
  Email,
  Settings,
  People,
  ColorLens,
  CheckCircle,
  HourglassEmpty,
  EmailOutlined,
  MarkEmailRead,
  MoreVert,
  Send,
  Refresh,
  FilterList,
  Warning,
  PersonAdd,
  Payment
} from '@mui/icons-material';
import { UseApi } from '../Hooks/UseApi';
import FeatureGuard from '../Subscription/FeatureGuard';
import { UseAuthRoles } from '../Hooks/UseAuthRoles';
import RequestDepositDialog from '../Payments/RequestDepositDialog';

const localizer = momentLocalizer(moment);

// Color scheme for different artists
const ARTIST_COLORS = [
  '#ff1744', // Red - Current user
  '#3f51b5', // Indigo
  '#4caf50', // Green
  '#ff9800', // Orange
  '#9c27b0', // Purple
  '#00bcd4', // Cyan
  '#795548', // Brown
  '#607d8b', // Blue Grey
  '#e91e63', // Pink
  '#009688'  // Teal
];

const AppointmentsPage = () => {
  const api = UseApi();
  const apiRef = useRef(api);
  
  const { getSubscriptionLevel } = UseAuthRoles();
  const currentTier = getSubscriptionLevel();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Existing state
  const [appointments, setAppointments] = useState([]);
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [newClient, setNewClient] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    confirmationEmails: false,
    reminderEmails: false
  });
  
  // Calendar sharing states
  const [studioMembers, setStudioMembers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [calendarSettings, setCalendarSettings] = useState({
    showAllAppointments: false,
    visibleArtists: new Set()
  });
  const [calendarSettingsOpen, setCalendarSettingsOpen] = useState(false);
  const [isStudioOwner, setIsStudioOwner] = useState(false);
  const [hasCalendarPermission, setHasCalendarPermission] = useState(false);
  
  // Confirmation management states
  const [tabValue, setTabValue] = useState(0);
  const [confirmationFilter, setConfirmationFilter] = useState('all');
  const [bulkActionMenu, setBulkActionMenu] = useState(null);
  const [selectedAppointments, setSelectedAppointments] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Deposit dialog states
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [depositDialogAppointment, setDepositDialogAppointment] = useState(null);
  const [defaultDepositAmount, setDefaultDepositAmount] = useState(null);
  
  // Appointment data model with confirmation fields
  const [newAppointment, setNewAppointment] = useState({
    id: '',
    client: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    date: '',
    hour: '1',
    minute: '00',
    ampm: 'AM',
    notes: '',
    duration: '60',
    deposit_amount: '',
    deposit_paid: false,
    reminder_email: false,
    confirmed: false,
    confirmed_at: null,
    confirmation_email_sent: false,
    confirmation_email_sent_at: null,
    is_walk_in: false
  });

  // Get confirmation status summary
  const getConfirmationSummary = useCallback(() => {
    const userAppointments = appointments.filter(apt => 
      apt.auth0_user_id === currentUser?.auth0_user_id
    );
    
    const confirmed = userAppointments.filter(apt => apt.confirmed).length;
    const pending = userAppointments.filter(apt => 
      !apt.confirmed && apt.confirmation_email_sent
    ).length;
    const noEmail = userAppointments.filter(apt => 
      !apt.confirmed && !apt.confirmation_email_sent
    ).length;
    
    return { total: userAppointments.length, confirmed, pending, noEmail };
  }, [appointments, currentUser]);

  // Filter appointments by confirmation status
  const getFilteredAppointments = useCallback(() => {
    const userAppointments = appointments.filter(apt => 
      apt.auth0_user_id === currentUser?.auth0_user_id
    );
    
    switch (confirmationFilter) {
      case 'confirmed':
        return userAppointments.filter(apt => apt.confirmed);
      case 'pending':
        return userAppointments.filter(apt => !apt.confirmed && apt.confirmation_email_sent);
      case 'no-email':
        return userAppointments.filter(apt => !apt.confirmed && !apt.confirmation_email_sent);
      default:
        return userAppointments;
    }
  }, [appointments, confirmationFilter, currentUser]);

  // Get confirmation status chip
  const getConfirmationStatusChip = (appointment) => {
    if (appointment.confirmed) {
      return (
        <Chip
          icon={<CheckCircle />}
          label="Confirmed"
          color="success"
          size="small"
          sx={{ ml: 1 }}
        />
      );
    } else if (appointment.confirmation_email_sent) {
      return (
        <Chip
          icon={<HourglassEmpty />}
          label="Pending"
          color="warning"
          size="small"
          sx={{ ml: 1 }}
        />
      );
    } else {
      return (
        <Chip
          icon={<EmailOutlined />}
          label="No Email"
          color="error"
          size="small"
          sx={{ ml: 1 }}
        />
      );
    }
  };

  // Enhanced event style getter with confirmation status
  const eventStyleGetter = useCallback((event) => {
    const isCurrentUser = event.artist_id === currentUser?.auth0_user_id;
    let backgroundColor = '#ff1744';
    
    if (!isCurrentUser && studioMembers.length > 0) {
      const artistIndex = studioMembers.findIndex(member => 
        member.user.username === event.artist_id
      );
      if (artistIndex !== -1) {
        backgroundColor = ARTIST_COLORS[(artistIndex + 1) % ARTIST_COLORS.length];
      }
    }
    
    const opacity = event.confirmed ? 1 : 0.7;
    const border = event.confirmed 
      ? `2px solid #4caf50`
      : !event.confirmation_email_sent
        ? `2px solid #f44336`
        : isCurrentUser 
          ? '2px solid #fff' 
          : 'none';
    
    return {
      style: {
        backgroundColor: backgroundColor,
        color: 'white',
        borderRadius: '5px',
        border: border,
        display: 'block',
        opacity: opacity,
        fontWeight: isCurrentUser ? 'bold' : 'normal'
      }
    };
  }, [studioMembers, currentUser]);

  // Function to get artist display name
  const getArtistDisplayName = useCallback((artistId) => {
    if (artistId === currentUser?.auth0_user_id) {
      return 'You';
    }
    
    const member = studioMembers.find(member => 
      member.user.username === artistId
    );
    
    return member ? 
      `${member.user.first_name} ${member.user.last_name}`.trim() || member.user.email :
      'Unknown Artist';
  }, [studioMembers, currentUser]);

  // Enhanced prepare events function with proper duration handling
  const prepareEvents = useCallback(() => {
    if (!appointments || !Array.isArray(appointments)) {
      return [];
    }
    
    return appointments
      .filter(appointment => {
        if (appointment.auth0_user_id === currentUser?.auth0_user_id) {
          return true;
        }
        
        if (calendarSettings.showAllAppointments && 
            calendarSettings.visibleArtists.has(appointment.auth0_user_id)) {
          return true;
        }
        
        return false;
      })
      .map((appointment) => {
        const startDateTime = new Date(`${appointment.date}T${appointment.time}`);
        const durationMinutes = appointment.duration || 60;
        const endDateTime = new Date(startDateTime.getTime() + (durationMinutes * 60 * 1000));
        
        return {
          id: appointment.id,
          title: `${appointment.client_name || 'Unknown Client'}`,
          start: startDateTime,
          end: endDateTime,
          notes: appointment.notes,
          client: appointment.client,
          artist_id: appointment.auth0_user_id,
          artist_name: getArtistDisplayName(appointment.auth0_user_id),
          is_own_appointment: appointment.auth0_user_id === currentUser?.auth0_user_id,
          duration: appointment.duration,
          deposit_amount: appointment.deposit_amount,
          deposit_paid: appointment.deposit_paid,
          reminder_email: appointment.reminder_email,
          confirmed: appointment.confirmed || false,
          confirmed_at: appointment.confirmed_at,
          confirmation_email_sent: appointment.confirmation_email_sent || false,
          confirmation_email_sent_at: appointment.confirmation_email_sent_at,
          client_email: appointment.client_email
        };
      });
  }, [appointments, calendarSettings, studioMembers, currentUser, getArtistDisplayName]);

  // Resend confirmation email
  const handleResendConfirmation = async (appointmentId) => {
    try {
      setError(null);
      const response = await apiRef.current.fetchWithAuth(`/appointments/${appointmentId}/resend-confirmation/`, {
        method: 'POST'
      });
      
      if (response.success) {
        setAppointments(prev => prev.map(apt => 
          apt.id === appointmentId 
            ? { ...apt, confirmation_email_sent: true, confirmation_email_sent_at: new Date().toISOString() }
            : apt
        ));
        setSuccess('Confirmation email sent successfully');
      } else {
        setError(response.message || 'Failed to resend confirmation email');
      }
    } catch (error) {
      console.error('Error resending confirmation:', error);
      setError('Failed to resend confirmation email');
    }
  };

  // Bulk resend confirmations
  const handleBulkResendConfirmations = async () => {
    setBulkActionLoading(true);
    try {
      const appointmentIds = Array.from(selectedAppointments);
      let successCount = 0;
      let errorCount = 0;
      
      for (const appointmentId of appointmentIds) {
        try {
          const response = await apiRef.current.fetchWithAuth(`/appointments/${appointmentId}/resend-confirmation/`, {
            method: 'POST'
          });
          if (response.success) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch {
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        setSuccess(`Successfully sent ${successCount} confirmation emails`);
        fetchData();
      }
      if (errorCount > 0) {
        setError(`Failed to send ${errorCount} confirmation emails`);
      }
      
      setSelectedAppointments(new Set());
    } catch (error) {
      setError('Failed to send bulk confirmation emails');
    } finally {
      setBulkActionLoading(false);
      setBulkActionMenu(null);
    }
  };

  // Handle appointment selection for bulk actions
  const handleAppointmentSelection = (appointmentId, selected) => {
    setSelectedAppointments(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(appointmentId);
      } else {
        newSet.delete(appointmentId);
      }
      return newSet;
    });
  };

  // Fetch studio members and permissions
  const fetchStudioMembers = async () => {
    try {
      const response = await apiRef.current.fetchWithAuth('/studio/members/');
      if (response.members) {
        setStudioMembers(response.members);
        setIsStudioOwner(response.is_owner || false);
        setHasCalendarPermission(response.can_view_calendars || false);
        setCurrentUser(response.current_user);
        
        if (response.can_view_calendars || response.is_owner) {
          const allArtistIds = response.members.map(member => member.user.username);
          setCalendarSettings(prev => ({
            ...prev,
            showAllAppointments: true,
            visibleArtists: new Set(allArtistIds)
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching studio members:', error);
    }
  };

  // Fetch default deposit amount from settings
  const fetchDefaultDeposit = async () => {
    try {
      const response = await apiRef.current.fetchWithAuth('/settings/default-deposit/');
      if (response && response.default_deposit_amount) {
        setDefaultDepositAmount(response.default_deposit_amount);
      }
    } catch (err) {
      // Non-critical â€” deposit settings may not exist yet
      console.log('No default deposit settings found');
    }
  };

  // Handle calendar visibility toggle
  const handleArtistVisibilityChange = (artistId, visible) => {
    setCalendarSettings(prev => {
      const newVisibleArtists = new Set(prev.visibleArtists);
      if (visible) {
        newVisibleArtists.add(artistId);
      } else {
        newVisibleArtists.delete(artistId);
      }
      
      return {
        ...prev,
        visibleArtists: newVisibleArtists
      };
    });
  };

  // Handle show all appointments toggle
  const handleShowAllAppointmentsChange = (show) => {
    setCalendarSettings(prev => ({
      ...prev,
      showAllAppointments: show,
      visibleArtists: show ? new Set(studioMembers.map(m => m.user.username)) : new Set()
    }));
  };

  // Email settings handlers
  const fetchEmailSettings = async () => {
    try {
      const response = await apiRef.current.fetchWithAuth('/appointments/email-settings/');
      setEmailSettings({
        confirmationEmails: response.confirmationEmails || false,
        reminderEmails: response.reminderEmails || false
      });
    } catch (error) {
      console.error('Error fetching email settings:', error);
    }
  };

  const handleEmailSettingChange = async (setting, value) => {
    try {
      const response = await apiRef.current.fetchWithAuth('/appointments/email-settings/', {
        method: 'POST',
        body: JSON.stringify({ [setting]: value })
      });
      
      if (response.success) {
        setEmailSettings(prev => ({ ...prev, [setting]: value }));
        setSuccess('Email settings updated successfully');
      } else {
        setError('Failed to update email settings');
      }
    } catch (error) {
      console.error('Error updating email settings:', error);
      setError('Failed to update email settings');
    }
  };

  // Open deposit dialog for an appointment
  const handleOpenDepositDialog = (appointment) => {
    setDepositDialogAppointment({
      id: appointment.id,
      client_name: appointment.client_name || appointment.title || `${appointment.first_name || ''} ${appointment.last_name || ''}`.trim(),
      date: appointment.date || (appointment.start ? moment(appointment.start).format('YYYY-MM-DD') : ''),
      time: appointment.time || (appointment.start ? moment(appointment.start).format('HH:mm') : ''),
      deposit_amount: appointment.deposit_amount || '',
      deposit_paid: appointment.deposit_paid || false,
      client_email: appointment.client_email || appointment.email || ''
    });
    setDepositDialogOpen(true);
  };

  // Handle successful deposit request
  const handleDepositRequested = (updatedAppointment) => {
    setAppointments(prev => prev.map(apt =>
      apt.id === updatedAppointment.id
        ? { ...apt, deposit_amount: updatedAppointment.deposit_amount }
        : apt
    ));
    setSuccess('Deposit payment link created successfully');
  };

  // Enhanced fetchData function
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      let validAppointments = [];
      let validClients = [];
      
      try {
        const appointmentsData = await apiRef.current.fetchAppointments();
        validAppointments = Array.isArray(appointmentsData) ? appointmentsData : 
                          (appointmentsData?.appointments || []);
      } catch (appointmentError) {
        console.error('Failed to fetch appointments:', appointmentError);
      }
      
      try {
        const clientsData = await apiRef.current.fetchClients();
        
        if (Array.isArray(clientsData)) {
          validClients = clientsData;
        } else if (clientsData && typeof clientsData === 'object') {
          if (clientsData.clients && Array.isArray(clientsData.clients)) {
            validClients = clientsData.clients;
          } else if (clientsData.data && Array.isArray(clientsData.data)) {
            validClients = clientsData.data;
          } else {
            validClients = [];
          }
        } else {
          validClients = [];
        }
        
      } catch (clientError) {
        console.error('Failed to fetch clients:', clientError);
        setError('Failed to load clients for search');
      }
      
      setAppointments(validAppointments);
      setClients(validClients);

      const today = moment().format('YYYY-MM-DD');
      setTodayAppointments(
        validAppointments.filter((app) => 
          app.date === today && 
          (!currentUser || app.auth0_user_id === currentUser.auth0_user_id)
        )
      );
      
      const nextWeek = moment().add(7, 'days').format('YYYY-MM-DD');
      setUpcomingAppointments(
        validAppointments.filter(
          (app) => 
            app.date > today && 
            app.date <= nextWeek &&
            (!currentUser || app.auth0_user_id === currentUser.auth0_user_id)
        ).sort((a, b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`))
      );
      
    } catch (err) {
      console.error('Error in fetchData:', err);
      setError('Failed to load data. Please try again.');
      
      setAppointments([]);
      setClients([]);
      setTodayAppointments([]);
      setUpcomingAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Fetch email settings, studio members, and default deposit on component mount
  useEffect(() => {
    fetchEmailSettings();
    fetchStudioMembers();
    fetchDefaultDeposit();
  }, []);

  // Update events when appointments or settings change
  useEffect(() => {
    const processedEvents = prepareEvents();
    setEvents(processedEvents);
  }, [appointments, prepareEvents]);

  // Fetch data when current user changes
  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser, fetchData]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditMode(false);
    setNewClient(false);
    setError(null);
  };

  const handleNewAppointmentChange = (e) => {
    const { name, value } = e.target;
    setNewAppointment((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setNewAppointment((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.trim() === '') {
      setFilteredClients([]);
      return;
    }
    
    const filtered = clients.filter((client) => {
      const firstName = client.first_name || '';
      const lastName = client.last_name || '';
      const fullName = `${firstName} ${lastName}`.toLowerCase().trim();
      const searchValue = value.toLowerCase().trim();
      return fullName.includes(searchValue);
    });
    
    setFilteredClients(filtered);
  };

  const handleClientSelect = (client) => {
    setNewAppointment({
      id: '',
      client: client.id,
      first_name: client.first_name,
      last_name: client.last_name,
      email: '',
      phone_number: '',
      date: moment().format('YYYY-MM-DD'),
      hour: '9',
      minute: '00',
      ampm: 'AM',
      notes: '',
      duration: '60',
      deposit_amount: '',
      deposit_paid: false,
      reminder_email: false,
      confirmed: false,
      confirmed_at: null,
      confirmation_email_sent: false,
      confirmation_email_sent_at: null,
      is_walk_in: false
    });
    setSearchTerm('');
    setFilteredClients([]);
    setOpenDialog(true);
    setNewClient(false);
    setEditMode(false);
    setError(null);
  };

  const handleDialogOpen = () => {
    setOpenDialog(true);
    setNewClient(true);
    setError(null);
    setNewAppointment({
      id: '',
      client: '',
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      date: moment().format('YYYY-MM-DD'),
      hour: '9',
      minute: '00',
      ampm: 'AM',
      notes: '',
      duration: '60',
      deposit_amount: '',
      deposit_paid: false,
      reminder_email: false,
      confirmed: false,
      confirmed_at: null,
      confirmation_email_sent: false,
      confirmation_email_sent_at: null,
      is_walk_in: false
    });
  };

  // Handle Walk-in appointment
  const handleWalkInClick = () => {
    setEditMode(false);
    setNewClient(true);
    setError(null);
    
    // Set current date/time for walk-in
    const now = moment();
    
    // Round minutes to nearest 15
    const currentMinute = now.minute();
    const roundedMinute = Math.floor(currentMinute / 15) * 15;
    
    setNewAppointment({
      id: '',
      client: '',
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      date: now.format('YYYY-MM-DD'),
      hour: now.format('h'),
      minute: roundedMinute.toString().padStart(2, '0'),
      ampm: now.format('A'),
      notes: 'Walk-in client',
      duration: '60',
      deposit_amount: '',
      deposit_paid: false,
      reminder_email: false,
      confirmed: true, // Walk-ins are auto-confirmed (they're here!)
      confirmed_at: now.toISOString(),
      confirmation_email_sent: false,
      confirmation_email_sent_at: null,
      is_walk_in: true
    });
    
    setOpenDialog(true);
  };

  const handleCalendarSelect = (event) => {
    const canEdit = event.is_own_appointment || 
                    (isStudioOwner && event.artist_id !== currentUser?.auth0_user_id);
    
    if (!canEdit) {
      setError('You can only edit your own appointments');
      return;
    }
    
    setEditMode(true);
    setError(null);
    setNewAppointment({
      id: event.id,
      client: event.client,
      first_name: event.title.split(' ')[0],
      last_name: event.title.split(' ')[1] || '',
      date: moment(event.start).format('YYYY-MM-DD'),
      hour: moment(event.start).format('hh'),
      minute: moment(event.start).format('mm'),
      ampm: moment(event.start).format('A'),
      notes: event.notes || '',
      duration: event.duration || '60',
      deposit_amount: event.deposit_amount || '',
      deposit_paid: event.deposit_paid || false,
      reminder_email: event.reminder_email || false,
      confirmed: event.confirmed || false,
      confirmed_at: event.confirmed_at || null,
      confirmation_email_sent: event.confirmation_email_sent || false,
      confirmation_email_sent_at: event.confirmation_email_sent_at || null,
      is_walk_in: event.is_walk_in || false,
      client_email: event.client_email || ''
    });
    setOpenDialog(true);
  };

  const handleSelectSlot = (slotInfo) => {
    setEditMode(false);
    setNewClient(true);
    setError(null);
    
    const slotDate = moment(slotInfo.start);
    
    setNewAppointment({
      id: '',
      client: '',
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      date: slotDate.format('YYYY-MM-DD'),
      hour: slotDate.format('hh'),
      minute: '00',
      ampm: slotDate.format('A'),
      notes: '',
      duration: '60',
      deposit_amount: '',
      deposit_paid: false,
      reminder_email: false,
      confirmed: false,
      confirmed_at: null,
      confirmation_email_sent: false,
      confirmation_email_sent_at: null,
      is_walk_in: false
    });
    
    setOpenDialog(true);
  };

  const handleAddOrUpdateAppointment = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const hour12 = parseInt(newAppointment.hour, 10);
      let hour24;
      if (newAppointment.ampm === "PM") {
        hour24 = (hour12 !== 12) ? hour12 + 12 : 12;
      } else {
        hour24 = (hour12 === 12) ? 0 : hour12;
      }
      const formattedHour24 = hour24.toString().padStart(2, '0');
      const timeStr = `${formattedHour24}:${newAppointment.minute}`;

      const appointmentData = {
        client: newAppointment.client,
        date: newAppointment.date,
        time: timeStr,
        notes: newAppointment.notes,
        is_walk_in: newAppointment.is_walk_in || false,
      };

      if (currentTier === 'standard' || currentTier === 'growth_studio') {
        Object.assign(appointmentData, {
          duration: newAppointment.duration,
          deposit_amount: newAppointment.deposit_amount,
          deposit_paid: newAppointment.deposit_paid,
          reminder_email: newAppointment.reminder_email
        });
      }

      if (editMode) {
        const updatedAppointment = await apiRef.current.fetchWithAuth(`/appointments/${newAppointment.id}/update/`, {
          method: 'PUT',
          body: JSON.stringify(appointmentData)
        });

        setAppointments((prevAppointments) =>
          prevAppointments.map((appointment) =>
            appointment.id === newAppointment.id ? { ...appointment, ...updatedAppointment } : appointment
          )
        );
        setSuccess('Appointment updated successfully');
      } else {
        if (newClient) {
          const clientData = {
            first_name: newAppointment.first_name,
            last_name: newAppointment.last_name,
            email: newAppointment.email || "",
            phone_number: newAppointment.phone_number || "",
          };
          
          const clientResponse = await apiRef.current.addClient(clientData);
          newAppointment.client = clientResponse.id;
          appointmentData.client = clientResponse.id;
        }

        const response = await apiRef.current.fetchWithAuth('/appointments/create/', {
          method: 'POST',
          body: JSON.stringify(appointmentData)
        });
        
        setAppointments((prevAppointments) => [...prevAppointments, response]);
        setSuccess('Appointment created successfully');
      }
      
      handleDialogClose();
    } catch (err) {
      console.error('Error adding/updating appointment:', err);
      setError(`Failed to ${editMode ? 'update' : 'add'} appointment: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAppointment = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await apiRef.current.fetchWithAuth(`/appointments/${newAppointment.id}/delete/`, {
        method: 'DELETE'
      });

      setAppointments((prevAppointments) =>
        prevAppointments.filter((appointment) => appointment.id !== newAppointment.id)
      );

      setSuccess('Appointment deleted successfully');
      handleDialogClose();
    } catch (err) {
      console.error('Error deleting appointment:', err);
      setError(`Failed to delete appointment: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const confirmationSummary = getConfirmationSummary();
  const filteredAppointmentsList = getFilteredAppointments();

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 } }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', md: 'center' }, 
          gap: 2,
          mb: 3 
        }}>
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
              Appointment Management
            </Typography>
            
            {/* Calendar sharing indicator */}
            {(hasCalendarPermission || isStudioOwner) && studioMembers.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <People sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Studio Calendar ({studioMembers.length} members)
                </Typography>
              </Stack>
            )}
            
            {/* Confirmation Summary */}
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Chip
                icon={<CheckCircle />}
                label={isMobile ? `${confirmationSummary.confirmed}` : `${confirmationSummary.confirmed} Confirmed`}
                color="success"
                size="small"
              />
              <Chip
                icon={<HourglassEmpty />}
                label={isMobile ? `${confirmationSummary.pending}` : `${confirmationSummary.pending} Pending`}
                color="warning"
                size="small"
              />
              <Chip
                icon={<Warning />}
                label={isMobile ? `${confirmationSummary.noEmail}` : `${confirmationSummary.noEmail} No Email`}
                color="error"
                size="small"
              />
            </Stack>
          </Box>
          
          {/* Calendar settings button */}
          {(hasCalendarPermission || isStudioOwner) && studioMembers.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<Settings />}
              onClick={() => setCalendarSettingsOpen(true)}
              size={isMobile ? "small" : "medium"}
              sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
            >
              {isMobile ? 'Settings' : 'Calendar Settings'}
            </Button>
          )}
        </Box>
        
        {loading && !openDialog && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        
        {/* Tabs for different views */}
        <Box sx={{ mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={(e, newValue) => setTabValue(newValue)}
            variant={isMobile ? "fullWidth" : "standard"}
          >
            <Tab 
              label={
                <Badge badgeContent={confirmationSummary.total} color="primary">
                  {isMobile ? 'Calendar' : 'Calendar View'}
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={confirmationSummary.pending + confirmationSummary.noEmail} color="error">
                  {isMobile ? 'Confirmations' : 'Confirmation Management'}
                </Badge>
              } 
            />
          </Tabs>
        </Box>
        
        {/* Calendar View Tab */}
        {tabValue === 0 && (
          <Grid container spacing={3}>
            {/* Main Calendar Section */}
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'stretch', sm: 'center' }, 
                  gap: 2,
                  mb: 2 
                }}>
                  <Typography variant={isMobile ? "h6" : "h5"}>
                    <DateRange sx={{ mr: 1, verticalAlign: 'bottom' }} />
                    {isMobile ? 'Calendar' : 'Appointment Calendar'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={!isMobile && <PersonAdd />}
                      onClick={handleWalkInClick}
                      disabled={loading}
                      size={isMobile ? "small" : "medium"}
                      sx={{ flex: { xs: 1, sm: 'none' } }}
                    >
                      {isMobile ? <><PersonAdd sx={{ mr: 0.5 }} /> Walk-in</> : 'Walk-in'}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={!isMobile && <AddCircleOutline />}
                      onClick={handleDialogOpen}
                      disabled={loading}
                      size={isMobile ? "small" : "medium"}
                      sx={{ flex: { xs: 1, sm: 'none' } }}
                    >
                      {isMobile ? <><AddCircleOutline sx={{ mr: 0.5 }} /> Add</> : 'Add Appointment'}
                    </Button>
                  </Box>
                </Box>
                
                {/* Color legend for shared calendars */}
                {calendarSettings.showAllAppointments && studioMembers.length > 0 && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      <ColorLens sx={{ fontSize: 16, mr: 1, verticalAlign: 'bottom' }} />
                      Calendar Legend
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Chip
                        avatar={<Box sx={{ width: 16, height: 16, bgcolor: '#ff1744', borderRadius: '50%' }} />}
                        label="Your Appointments"
                        size="small"
                        variant="outlined"
                      />
                      {studioMembers.map((member, index) => (
                        calendarSettings.visibleArtists.has(member.user.username) && (
                          <Chip
                            key={member.user.username}
                            avatar={<Box sx={{ 
                              width: 16, 
                              height: 16, 
                              bgcolor: ARTIST_COLORS[(index + 1) % ARTIST_COLORS.length], 
                              borderRadius: '50%' 
                            }} />}
                            label={`${member.user.first_name} ${member.user.last_name}`.trim() || member.user.email}
                            size="small"
                            variant="outlined"
                          />
                        )
                      ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Green border = Confirmed | Red border = No email sent | Default = Pending confirmation
                    </Typography>
                  </Box>
                )}
                
                <TextField
                  label="Search Clients"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  fullWidth
                  sx={{ mb: 2 }}
                  placeholder="Type client name to search..."
                />
                
                {filteredClients.length > 0 && (
                  <Paper elevation={1} sx={{ mb: 2, maxHeight: '200px', overflow: 'auto' }}>
                    <List>
                      {filteredClients.map((client) => (
                        <ListItem key={client.id} button onClick={() => handleClientSelect(client)}>
                          <ListItemText primary={`${client.first_name} ${client.last_name}`} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
                
                {searchTerm && filteredClients.length === 0 && clients.length > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No clients found matching "{searchTerm}". Try a different search term.
                  </Alert>
                )}
                
                <Box 
                  sx={{ 
                    height: { xs: 350, sm: 400, md: 500 }, 
                    position: 'relative',
                    zIndex: 1, 
                    backgroundColor: 'white',
                    borderRadius: 1,
                    '& .rbc-calendar': {
                      width: '100%',
                      height: '100%'
                    },
                    // Mobile calendar toolbar fixes
                    '& .rbc-toolbar': {
                      flexWrap: 'wrap',
                      gap: '4px',
                      justifyContent: 'center',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    },
                    '& .rbc-toolbar button': {
                      padding: { xs: '2px 6px', sm: '4px 12px' },
                      fontSize: { xs: '0.7rem', sm: '0.85rem' },
                    },
                    '& .rbc-toolbar-label': {
                      width: { xs: '100%', sm: 'auto' },
                      textAlign: 'center',
                      order: { xs: -1, sm: 0 },
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      fontWeight: 600,
                    },
                    // Compact event text on mobile
                    '& .rbc-event-content': {
                      fontSize: { xs: '0.65rem', sm: '0.8rem' },
                    },
                    '& .rbc-show-more': {
                      fontSize: { xs: '0.65rem', sm: '0.8rem' },
                    },
                  }}
                >
                  {events.length > 0 ? (
                    <Calendar
                      localizer={localizer}
                      events={events}
                      startAccessor="start"
                      endAccessor="end"
                      style={{ height: '100%' }}
                      onSelectEvent={handleCalendarSelect}
                      onSelectSlot={handleSelectSlot}
                      selectable={true}
                      eventPropGetter={eventStyleGetter}
                      views={['month', 'week', 'day']}
                      defaultView="month"
                      components={{
                        event: ({ event }) => (
                          <Tooltip 
                            title={
                              <Box>
                                <Typography variant="body2">{event.artist_name} - {event.title}</Typography>
                                <Typography variant="caption" display="block">
                                  Status: {event.confirmed ? 'Confirmed âœ“' : 'Pending confirmation'}
                                </Typography>
                                {event.confirmed && event.confirmed_at && (
                                  <Typography variant="caption" display="block">
                                    Confirmed: {new Date(event.confirmed_at).toLocaleDateString()}
                                  </Typography>
                                )}
                              </Box>
                            } 
                            arrow
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {event.confirmed && <CheckCircle sx={{ fontSize: 12, color: '#4caf50' }} />}
                              <span>
                                {event.is_own_appointment ? event.title : `${event.artist_name}: ${event.title}`}
                              </span>
                            </Box>
                          </Tooltip>
                        )
                      }}
                    />
                  ) : loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Calendar
                      localizer={localizer}
                      events={[]}
                      startAccessor="start"
                      endAccessor="end"
                      style={{ height: '100%' }}
                      onSelectSlot={handleSelectSlot}
                      selectable={true}
                      views={['month', 'week', 'day']}
                      defaultView="month"
                    />
                  )}
                </Box>
              </Paper>
            </Grid>
            
            {/* Today's Appointments - Top Left */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                  <EventAvailable sx={{ mr: 1, verticalAlign: 'bottom', fontSize: isMobile ? 18 : 24 }} />
                  Today's Appointments
                </Typography>
                {loading ? (
                  <CircularProgress size={20} />
                ) : todayAppointments.length > 0 ? (
                  <List>
                    {todayAppointments.map((appointment) => (
                      <ListItem 
                        key={appointment.id} 
                        button 
                        onClick={() => handleCalendarSelect({
                          id: appointment.id,
                          title: appointment.client_name || 'Unknown Client',
                          start: new Date(`${appointment.date}T${appointment.time}`),
                          notes: appointment.notes,
                          client: appointment.client,
                          is_own_appointment: true,
                          confirmed: appointment.confirmed,
                          confirmation_email_sent: appointment.confirmation_email_sent,
                          client_email: appointment.client_email
                        })}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span>{appointment.client_name || 'Unknown Client'}</span>
                              {getConfirmationStatusChip(appointment)}
                            </Box>
                          }
                          secondary={`Time: ${appointment.time}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography>No appointments for today.</Typography>
                )}
              </Paper>
            </Grid>
            
            {/* Email Reminders - Top Right */}
            <Grid item xs={12} md={6}>
              <FeatureGuard feature="email_reminders" showPreview={true}>
                <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                    <Email sx={{ mr: 1, verticalAlign: 'bottom', fontSize: isMobile ? 18 : 24 }} />
                    Email Reminders
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Configure automatic email reminders to be sent to clients before their appointments.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <FormControlLabel
                            control={
                              <Switch 
                                checked={emailSettings.confirmationEmails} 
                                onChange={(e) => handleEmailSettingChange('confirmationEmails', e.target.checked)}
                              />
                            }
                            label="Enable appointment confirmation emails"
                          />
                          <Typography variant="caption" color="textSecondary" display="block">
                            Automatically send confirmation emails when appointments are booked
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <FormControlLabel
                            control={
                              <Switch 
                                checked={emailSettings.reminderEmails} 
                                onChange={(e) => handleEmailSettingChange('reminderEmails', e.target.checked)}
                              />
                            }
                            label="Enable appointment reminder emails"
                          />
                          <Typography variant="caption" color="textSecondary" display="block">
                            Send reminder emails 24 hours before scheduled appointments
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Paper>
              </FeatureGuard>
            </Grid>
            
            {/* Upcoming Appointments - Full Width Bottom */}
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                  <Timeline sx={{ mr: 1, verticalAlign: 'bottom', fontSize: isMobile ? 18 : 24 }} />
                  {isMobile ? 'Upcoming (7 Days)' : 'Your Upcoming Appointments (Next 7 Days)'}
                </Typography>
                {loading ? (
                  <CircularProgress size={20} />
                ) : upcomingAppointments.length > 0 ? (
                  <Grid container spacing={2}>
                    {upcomingAppointments.map((appointment) => (
                      <Grid item xs={12} sm={6} md={4} key={appointment.id}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 2 }
                          }}
                          onClick={() => handleCalendarSelect({
                            id: appointment.id,
                            title: appointment.client_name || 'Unknown Client',
                            start: new Date(`${appointment.date}T${appointment.time}`),
                            notes: appointment.notes,
                            client: appointment.client,
                            is_own_appointment: true,
                            confirmed: appointment.confirmed,
                            confirmation_email_sent: appointment.confirmation_email_sent,
                            client_email: appointment.client_email
                          })}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {appointment.client_name || 'Unknown Client'}
                              </Typography>
                              {getConfirmationStatusChip(appointment)}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {moment(appointment.date).format('ddd, MMM DD')} at {appointment.time}
                            </Typography>
                            {appointment.notes && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                {appointment.notes.length > 50 ? `${appointment.notes.substring(0, 50)}...` : appointment.notes}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography>No upcoming appointments for the next 7 days.</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
        
        {/* Confirmation Management Tab */}
        {tabValue === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'stretch', md: 'center' }, 
                  gap: 2,
                  mb: 3 
                }}>
                  <Typography variant={isMobile ? "h6" : "h5"}>
                    <MarkEmailRead sx={{ mr: 1, verticalAlign: 'bottom' }} />
                    {isMobile ? 'Confirmations' : 'Confirmation Management'}
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    alignItems: 'center',
                    flexWrap: 'wrap'
                  }}>
                    <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 150 }, flex: { xs: 1, sm: 'none' } }}>
                      <InputLabel>Filter</InputLabel>
                      <Select
                        value={confirmationFilter}
                        label="Filter"
                        onChange={(e) => setConfirmationFilter(e.target.value)}
                        startAdornment={<FilterList sx={{ mr: 1 }} />}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="confirmed">Confirmed</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="no-email">No Email</MenuItem>
                      </Select>
                    </FormControl>
                    
                    {selectedAppointments.size > 0 && (
                      <>
                        <IconButton
                          onClick={(e) => setBulkActionMenu(e.currentTarget)}
                          disabled={bulkActionLoading}
                        >
                          <MoreVert />
                        </IconButton>
                        <Menu
                          anchorEl={bulkActionMenu}
                          open={Boolean(bulkActionMenu)}
                          onClose={() => setBulkActionMenu(null)}
                        >
                          <MenuItem onClick={handleBulkResendConfirmations} disabled={bulkActionLoading}>
                            <ListItemIcon>
                              {bulkActionLoading ? <CircularProgress size={20} /> : <Send />}
                            </ListItemIcon>
                            Resend Confirmations ({selectedAppointments.size})
                          </MenuItem>
                        </Menu>
                      </>
                    )}
                    
                    {isMobile ? (
                      <IconButton
                        onClick={fetchData}
                        disabled={loading}
                        size="small"
                      >
                        <Refresh />
                      </IconButton>
                    ) : (
                      <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={fetchData}
                        disabled={loading}
                      >
                        Refresh
                      </Button>
                    )}
                  </Box>
                </Box>
                
                {/* Desktop Table View */}
                {!isMobile && (
                <TableContainer>
                  <Table size="medium">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={selectedAppointments.size > 0 && selectedAppointments.size < filteredAppointmentsList.length}
                            checked={filteredAppointmentsList.length > 0 && selectedAppointments.size === filteredAppointmentsList.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedAppointments(new Set(filteredAppointmentsList.map(apt => apt.id)));
                              } else {
                                setSelectedAppointments(new Set());
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Date & Time</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Deposit</TableCell>
                        <TableCell>Email Sent</TableCell>
                        <TableCell>Confirmed</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAppointmentsList.map((appointment) => (
                        <TableRow key={appointment.id}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedAppointments.has(appointment.id)}
                              onChange={(e) => handleAppointmentSelection(appointment.id, e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {appointment.client_name || 'Unknown Client'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {moment(appointment.date).format('MMM DD, YYYY')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {appointment.time}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {getConfirmationStatusChip(appointment)}
                          </TableCell>
                          <TableCell>
                            {appointment.deposit_paid ? (
                              <Chip
                                icon={<CheckCircle />}
                                label={appointment.deposit_amount ? `$${appointment.deposit_amount}` : 'Paid'}
                                color="success"
                                size="small"
                              />
                            ) : appointment.deposit_amount && parseFloat(appointment.deposit_amount) > 0 ? (
                              <Chip
                                icon={<HourglassEmpty />}
                                label={`$${appointment.deposit_amount}`}
                                color="warning"
                                size="small"
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {appointment.confirmation_email_sent ? (
                              <Box>
                                <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {appointment.confirmation_email_sent_at && 
                                    moment(appointment.confirmation_email_sent_at).format('MMM DD, HH:mm')
                                  }
                                </Typography>
                              </Box>
                            ) : (
                              <Box>
                                <EmailOutlined sx={{ color: 'text.secondary', fontSize: 16 }} />
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Not sent
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            {appointment.confirmed ? (
                              <Box>
                                <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {appointment.confirmed_at && 
                                    moment(appointment.confirmed_at).format('MMM DD, HH:mm')
                                  }
                                </Typography>
                              </Box>
                            ) : (
                              <HourglassEmpty sx={{ color: 'warning.main', fontSize: 16 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {!appointment.deposit_paid && (
                                <Tooltip title="Request Deposit Payment">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    startIcon={<Payment />}
                                    onClick={() => handleOpenDepositDialog(appointment)}
                                  >
                                    Deposit
                                  </Button>
                                </Tooltip>
                              )}
                              {!appointment.confirmed && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Send />}
                                  onClick={() => handleResendConfirmation(appointment.id)}
                                >
                                  Resend
                                </Button>
                              )}
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => handleCalendarSelect({
                                  id: appointment.id,
                                  title: appointment.client_name || 'Unknown Client',
                                  start: new Date(`${appointment.date}T${appointment.time}`),
                                  notes: appointment.notes,
                                  client: appointment.client,
                                  is_own_appointment: true,
                                  confirmed: appointment.confirmed,
                                  confirmation_email_sent: appointment.confirmation_email_sent,
                                  confirmed_at: appointment.confirmed_at,
                                  confirmation_email_sent_at: appointment.confirmation_email_sent_at,
                                  client_email: appointment.client_email
                                })}
                              >
                                Edit
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                )}
                
                {/* Mobile Card View */}
                {isMobile && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* Select all on mobile */}
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                      <Checkbox
                        size="small"
                        indeterminate={selectedAppointments.size > 0 && selectedAppointments.size < filteredAppointmentsList.length}
                        checked={filteredAppointmentsList.length > 0 && selectedAppointments.size === filteredAppointmentsList.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAppointments(new Set(filteredAppointmentsList.map(apt => apt.id)));
                          } else {
                            setSelectedAppointments(new Set());
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Select all ({filteredAppointmentsList.length})
                      </Typography>
                    </Box>
                    
                    {filteredAppointmentsList.map((appointment) => (
                      <Card key={appointment.id} variant="outlined" sx={{ position: 'relative' }}>
                        <CardContent sx={{ pb: '12px !important', pt: 1.5, px: 2 }}>
                          {/* Top row: checkbox + name + status */}
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Checkbox
                              size="small"
                              checked={selectedAppointments.has(appointment.id)}
                              onChange={(e) => handleAppointmentSelection(appointment.id, e.target.checked)}
                              sx={{ p: 0, mr: 1 }}
                            />
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {appointment.client_name || 'Unknown Client'}
                            </Typography>
                            {getConfirmationStatusChip(appointment)}
                          </Box>
                          
                          {/* Info row */}
                          <Box sx={{ display: 'flex', gap: 2, mb: 1.5, pl: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              {moment(appointment.date).format('MMM DD')} \u00B7 {appointment.time}
                            </Typography>
                            {appointment.deposit_paid ? (
                              <Chip icon={<CheckCircle />} label={appointment.deposit_amount ? `$${appointment.deposit_amount}` : 'Paid'} color="success" size="small" sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' }, '& .MuiChip-icon': { fontSize: 12 } }} />
                            ) : appointment.deposit_amount && parseFloat(appointment.deposit_amount) > 0 ? (
                              <Chip icon={<HourglassEmpty />} label={`$${appointment.deposit_amount}`} color="warning" size="small" sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' }, '& .MuiChip-icon': { fontSize: 12 } }} />
                            ) : null}
                            {appointment.confirmation_email_sent && (
                              <Tooltip title={appointment.confirmation_email_sent_at ? `Sent ${moment(appointment.confirmation_email_sent_at).format('MMM DD, HH:mm')}` : 'Email sent'}>
                                <MarkEmailRead sx={{ fontSize: 14, color: 'success.main' }} />
                              </Tooltip>
                            )}
                          </Box>
                          
                          {/* Action buttons */}
                          <Box sx={{ display: 'flex', gap: 1, pl: 3, flexWrap: 'wrap' }}>
                            {!appointment.deposit_paid && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                onClick={() => handleOpenDepositDialog(appointment)}
                                sx={{ fontSize: '0.7rem', py: 0.25, minWidth: 0, px: 1 }}
                              >
                                <Payment sx={{ fontSize: 14, mr: 0.5 }} /> Deposit
                              </Button>
                            )}
                            {!appointment.confirmed && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleResendConfirmation(appointment.id)}
                                sx={{ fontSize: '0.7rem', py: 0.25, minWidth: 0, px: 1 }}
                              >
                                <Send sx={{ fontSize: 14, mr: 0.5 }} /> Resend
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => handleCalendarSelect({
                                id: appointment.id,
                                title: appointment.client_name || 'Unknown Client',
                                start: new Date(`${appointment.date}T${appointment.time}`),
                                notes: appointment.notes,
                                client: appointment.client,
                                is_own_appointment: true,
                                confirmed: appointment.confirmed,
                                confirmation_email_sent: appointment.confirmation_email_sent,
                                confirmed_at: appointment.confirmed_at,
                                confirmation_email_sent_at: appointment.confirmation_email_sent_at,
                                client_email: appointment.client_email
                              })}
                              sx={{ fontSize: '0.7rem', py: 0.25, minWidth: 0, px: 1 }}
                            >
                              Edit
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
                
                {filteredAppointmentsList.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No appointments found for the selected filter.
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Calendar Settings Dialog */}
      <Dialog open={calendarSettingsOpen} onClose={() => setCalendarSettingsOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Settings sx={{ mr: 1 }} />
            Calendar Sharing Settings
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Control which studio members' calendars are visible to you. Color coding helps distinguish between different artists.
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={calendarSettings.showAllAppointments}
                onChange={(e) => handleShowAllAppointmentsChange(e.target.checked)}
              />
            }
            label="Show Studio Appointments"
            sx={{ mb: 3 }}
          />
          
          {calendarSettings.showAllAppointments && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Select Artists to View:
              </Typography>
              <FormGroup>
                {studioMembers.map((member, index) => (
                  <FormControlLabel
                    key={member.user.username}
                    control={
                      <Checkbox
                        checked={calendarSettings.visibleArtists.has(member.user.username)}
                        onChange={(e) => handleArtistVisibilityChange(member.user.username, e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box 
                          sx={{ 
                            width: 16, 
                            height: 16, 
                            bgcolor: ARTIST_COLORS[(index + 1) % ARTIST_COLORS.length], 
                            borderRadius: '50%',
                            mr: 1
                          }} 
                        />
                        {`${member.user.first_name} ${member.user.last_name}`.trim() || member.user.email}
                        <Chip 
                          label={member.role} 
                          size="small" 
                          variant="outlined" 
                          sx={{ ml: 1, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalendarSettingsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Appointment Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {editMode ? 'Edit Appointment' : 'New Appointment'}
            {isMobile && (
              <Button onClick={handleDialogClose} color="inherit" size="small">
                Close
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, md: 3 } }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {/* Appointment Type Selection */}
          {!editMode && (
            <Box sx={{ mb: 3, mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Appointment Type
              </Typography>
              <FormControl component="fieldset">
                <Stack direction="row" spacing={2}>
                  <Chip
                    icon={<AddCircleOutline />}
                    label="Scheduled Appointment"
                    onClick={() => setNewAppointment(prev => ({ ...prev, is_walk_in: false, notes: prev.notes === 'Walk-in client' ? '' : prev.notes }))}
                    color={!newAppointment.is_walk_in ? 'primary' : 'default'}
                    variant={!newAppointment.is_walk_in ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer', py: 2.5, px: 1 }}
                  />
                  <Chip
                    icon={<PersonAdd />}
                    label="Walk-in"
                    onClick={() => setNewAppointment(prev => ({ ...prev, is_walk_in: true, confirmed: true, confirmed_at: new Date().toISOString(), notes: prev.notes || 'Walk-in client' }))}
                    color={newAppointment.is_walk_in ? 'secondary' : 'default'}
                    variant={newAppointment.is_walk_in ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer', py: 2.5, px: 1 }}
                  />
                </Stack>
              </FormControl>
              {newAppointment.is_walk_in && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Walk-ins are automatically confirmed since the client is present.
                </Typography>
              )}
            </Box>
          )}
          
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Basic Information
          </Typography>
          {newClient && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  name="first_name"
                  value={newAppointment.first_name}
                  onChange={handleNewAppointmentChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  name="last_name"
                  value={newAppointment.last_name}
                  onChange={handleNewAppointmentChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="email"
                  value={newAppointment.email}
                  onChange={handleNewAppointmentChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone Number"
                  name="phone_number"
                  value={newAppointment.phone_number}
                  onChange={handleNewAppointmentChange}
                  fullWidth
                />
              </Grid>
            </Grid>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date"
                name="date"
                type="date"
                value={newAppointment.date}
                onChange={handleNewAppointmentChange}
                fullWidth
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>Hour</InputLabel>
                <Select
                  label="Hour"
                  name="hour"
                  value={newAppointment.hour}
                  onChange={handleNewAppointmentChange}
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <MenuItem key={i + 1} value={String(i + 1)}>
                      {i + 1}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4} sm={2}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>Min</InputLabel>
                <Select
                  label="Min"
                  name="minute"
                  value={newAppointment.minute}
                  onChange={handleNewAppointmentChange}
                >
                  {['00', '15', '30', '45'].map((minute) => (
                    <MenuItem key={minute} value={minute}>
                      {minute}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4} sm={2}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>AM/PM</InputLabel>
                <Select
                  label="AM/PM"
                  name="ampm"
                  value={newAppointment.ampm}
                  onChange={handleNewAppointmentChange}
                >
                  <MenuItem value="AM">AM</MenuItem>
                  <MenuItem value="PM">PM</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                name="notes"
                value={newAppointment.notes}
                onChange={handleNewAppointmentChange}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Additional Details
              {!(currentTier === 'standard' || currentTier === 'growth_studio') && (
                <Chip 
                  label="Standard+ Plan Feature" 
                  size="small" 
                  color="primary" 
                  sx={{ ml: 1 }} 
                />
              )}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Duration (minutes)"
                  name="duration"
                  type="number"
                  value={newAppointment.duration}
                  onChange={handleNewAppointmentChange}
                  fullWidth
                  disabled={!(currentTier === 'standard' || currentTier === 'growth_studio')}
                  InputProps={{ inputProps: { min: 15, step: 15 } }}
                  sx={{
                    '& .Mui-disabled': {
                      bgcolor: 'rgba(0, 0, 0, 0.05)'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Deposit Amount"
                  name="deposit_amount"
                  type="number"
                  value={newAppointment.deposit_amount}
                  onChange={handleNewAppointmentChange}
                  fullWidth
                  disabled={!(currentTier === 'standard' || currentTier === 'growth_studio')}
                  InputProps={{ 
                    startAdornment: <AttachMoney />,
                    inputProps: { min: 0, step: 5 }
                  }}
                  sx={{
                    '& .Mui-disabled': {
                      bgcolor: 'rgba(0, 0, 0, 0.05)'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newAppointment.deposit_paid}
                      onChange={handleSwitchChange}
                      name="deposit_paid"
                      disabled={!(currentTier === 'standard' || currentTier === 'growth_studio')}
                    />
                  }
                  label="Deposit Paid"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newAppointment.reminder_email}
                      onChange={handleSwitchChange}
                      name="reminder_email"
                      disabled={!(currentTier === 'standard' || currentTier === 'growth_studio')}
                    />
                  }
                  label="Send Email Reminder"
                />
              </Grid>

              {/* Request Deposit Button - inside edit dialog */}
              {editMode && !newAppointment.deposit_paid && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<Payment />}
                    onClick={() => {
                      handleDialogClose();
                      handleOpenDepositDialog({
                        id: newAppointment.id,
                        client_name: `${newAppointment.first_name} ${newAppointment.last_name}`.trim(),
                        date: newAppointment.date,
                        time: `${newAppointment.hour}:${newAppointment.minute} ${newAppointment.ampm}`,
                        deposit_amount: newAppointment.deposit_amount,
                        deposit_paid: newAppointment.deposit_paid,
                        client_email: newAppointment.client_email || newAppointment.email || ''
                      });
                    }}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    Request Deposit via Stripe
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                    Send a secure Stripe payment link to collect the deposit â€” zero platform fees
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
          
          {/* Confirmation Status Section */}
          {editMode && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Confirmation Status
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {newAppointment.confirmed ? (
                              <>
                                <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                                <Box>
                                  <Typography variant="body2" color="success.main" fontWeight="bold">
                                    Appointment Confirmed
                                  </Typography>
                                  {newAppointment.confirmed_at && (
                                    <Typography variant="caption" color="text.secondary">
                                      Confirmed on: {new Date(newAppointment.confirmed_at).toLocaleString()}
                                    </Typography>
                                  )}
                                </Box>
                              </>
                            ) : (
                              <>
                                <HourglassEmpty sx={{ color: 'warning.main', mr: 1 }} />
                                <Box>
                                  <Typography variant="body2" color="warning.main" fontWeight="bold">
                                    Pending Confirmation
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Client has not confirmed this appointment yet
                                  </Typography>
                                </Box>
                              </>
                            )}
                          </Box>
                          
                          {!newAppointment.confirmed && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<EmailOutlined />}
                              onClick={() => handleResendConfirmation(newAppointment.id)}
                            >
                              Resend Email
                            </Button>
                          )}
                        </Box>
                        
                        {/* Email Status */}
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Email Status:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            {newAppointment.confirmation_email_sent ? (
                              <>
                                <MarkEmailRead sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                                <Typography variant="body2">
                                  Confirmation email sent
                                </Typography>
                                {newAppointment.confirmation_email_sent_at && (
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    ({new Date(newAppointment.confirmation_email_sent_at).toLocaleString()})
                                  </Typography>
                                )}
                              </>
                            ) : (
                              <>
                                <EmailOutlined sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                  No confirmation email sent
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, md: 3 }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Button onClick={handleDialogClose} color="inherit" sx={{ order: { xs: 3, sm: 0 } }}>
            Cancel
          </Button>
          {editMode && (
            <Button onClick={handleDeleteAppointment} color="error" disabled={loading} sx={{ order: { xs: 2, sm: 0 } }}>
              Delete
            </Button>
          )}
          <Button 
            onClick={handleAddOrUpdateAppointment} 
            variant="contained" 
            color="primary"
            disabled={loading}
            fullWidth={isMobile}
            sx={{ order: { xs: 1, sm: 0 } }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : editMode ? (
              'Update Appointment'
            ) : (
              'Add Appointment'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Deposit Dialog */}
      <RequestDepositDialog
        open={depositDialogOpen}
        onClose={() => {
          setDepositDialogOpen(false);
          setDepositDialogAppointment(null);
        }}
        appointment={depositDialogAppointment}
        defaultDepositAmount={defaultDepositAmount}
        onDepositRequested={handleDepositRequested}
      />
    </Container>
  );
};

export default AppointmentsPage;